import React from "react";
import { formatCurrency, formatCurrencyCompact } from "../../utils/formatters";
import DemoCTA from "./DemoCTA";

function DollarIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    );
}

function RepeatIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
    );
}

function ShieldIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

function TrendingDownIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
            <polyline points="16 17 22 17 22 11" />
        </svg>
    );
}

function RefreshIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
    );
}

const demoInsights = [
    {
        id: "demo-1",
        type: "IDLE_CASH",
        title: "Idle Cash",
        description: "$8,400 in checking earning nothing",
        action: "Move excess cash to a high-yield savings account earning 4.5% APY.",
        dollarImpact: 378,
        status: "VIEWED",
        metadata: {},
    },
    {
        id: "demo-2",
        type: "RECURRING_CHARGES",
        title: "Recurring Charges",
        description: "6 subscriptions totaling $127/month ($1,524/year)",
        action: "Review your active subscriptions and cancel any you no longer use.",
        dollarImpact: 1524,
        status: "VIEWED",
        metadata: {
            subscriptions: [
                { merchant: "Netflix", amount: 15.49, lastDate: "2026-03-15" },
                { merchant: "Spotify", amount: 10.99, lastDate: "2026-03-12" },
                { merchant: "Adobe Creative Cloud", amount: 54.99, lastDate: "2026-03-08" },
                { merchant: "ChatGPT Plus", amount: 20.00, lastDate: "2026-03-05" },
                { merchant: "iCloud+", amount: 9.99, lastDate: "2026-03-01" },
                { merchant: "NYT Digital", amount: 17.00, lastDate: "2026-02-28" },
            ],
        },
    },
    {
        id: "demo-3",
        type: "EMERGENCY_FUND_HIGH",
        title: "Emergency Fund Excess",
        description: "Savings covers 9.2 months, $12,000 excess could be invested",
        action: "Consider moving excess emergency funds into a diversified index fund for better long-term returns.",
        dollarImpact: 840,
        status: "VIEWED",
        metadata: {},
    },
    {
        id: "demo-4",
        type: "SAVINGS_RATE_DROP",
        title: "Savings Rate Drop",
        description: "Rate dropped from 42% to 31%, food spending up $340",
        action: "Review your food and dining spending to identify areas to cut back.",
        dollarImpact: 400,
        status: "VIEWED",
        metadata: {},
    },
];

function getInsightIcon(type) {
    switch (type) {
        case "IDLE_CASH":
            return { icon: DollarIcon, bg: "border border-gray-200", color: "text-gray-700" };
        case "RECURRING_CHARGES":
            return { icon: RepeatIcon, bg: "border border-gray-200", color: "text-gray-700" };
        case "EMERGENCY_FUND_LOW":
            return { icon: ShieldIcon, bg: "border border-gray-200", color: "text-gray-700" };
        case "EMERGENCY_FUND_HIGH":
            return { icon: ShieldIcon, bg: "border border-gray-200", color: "text-gray-700" };
        case "SAVINGS_RATE_DROP":
            return { icon: TrendingDownIcon, bg: "border border-gray-200", color: "text-gray-700" };
        default:
            return { icon: DollarIcon, bg: "border border-gray-200", color: "text-gray-700" };
    }
}

export default function DemoInsightsTab() {
    const totalImpact = demoInsights.reduce((sum, i) => sum + (i.dollarImpact || 0), 0);

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Insights</h1>
                <button
                    onClick={() => alert("Sign up to use this feature")}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                >
                    <RefreshIcon className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Summary banner */}
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 mb-6">
                <p className="text-gray-700 text-sm">
                    We found {demoInsights.length} opportunities worth an estimated
                </p>
                <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-accent-600 text-3xl font-bold">
                        {formatCurrencyCompact(totalImpact)}
                    </span>
                    <span className="text-gray-500">/year</span>
                </div>
            </div>

            {/* Insight cards */}
            <div className="space-y-4">
                {demoInsights.map((insight) => {
                    const { icon: Icon, bg, color } = getInsightIcon(insight.type);
                    return (
                        <div key={insight.id} className="card p-6">
                            <div className="flex items-start gap-4">
                                {/* Icon */}
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bg} shrink-0`}>
                                    <Icon className={`w-5 h-5 ${color}`} />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                        <h3 className="text-gray-900 font-semibold">{insight.title}</h3>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {/* Dollar impact badge */}
                                            {insight.dollarImpact != null && (
                                                <div className="bg-accent-50 text-accent-600 text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                                                    +{formatCurrencyCompact(insight.dollarImpact)}/year
                                                </div>
                                            )}
                                            {/* Dismiss button */}
                                            <button
                                                onClick={() => alert("Sign up to use this feature")}
                                                className="text-gray-400 hover:text-gray-500 p-1"
                                            >
                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-gray-500 text-sm mt-1">{insight.description}</p>

                                    {insight.action && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3 text-gray-700 text-sm">
                                            {insight.action}
                                        </div>
                                    )}

                                    {/* Subscription table for RECURRING_CHARGES */}
                                    {insight.type === "RECURRING_CHARGES" && insight.metadata?.subscriptions?.length > 0 && (
                                        <table className="mt-3 w-full text-sm">
                                            <thead>
                                                <tr className="text-gray-500 text-left">
                                                    <th className="pb-2 font-medium">Merchant</th>
                                                    <th className="pb-2 font-medium text-right">Amount</th>
                                                    <th className="pb-2 font-medium text-right">Last Charged</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {insight.metadata.subscriptions.map((sub, idx) => (
                                                    <tr key={idx} className="text-gray-500">
                                                        <td className="py-1">{sub.merchant}</td>
                                                        <td className="py-1 text-right">{formatCurrency(sub.amount)}</td>
                                                        <td className="py-1 text-right">{sub.lastDate}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-8">
                <DemoCTA variant="inline" />
            </div>
        </div>
    );
}
